(function(global) {
  if (global.SmsLengthPreview) return;

  const URL_PATTERN = /https?:\/\/[^\s\]]+/gi;
  const TRAILING_URL_PUNCTUATION = /[.,;:!?)]+$/;
  const APPOINTMENT_TOKEN_LENGTH = 10;
  const PURL_FORM_TOKEN_LENGTH = 64;
  const APPOINTMENT_PATH_PATTERN = new RegExp(`^/a/[A-Za-z0-9_-]{${APPOINTMENT_TOKEN_LENGTH}}$`);
  const PURL_PERSONALIZED_PATH_PATTERN =
    new RegExp(`^/forms/inquiries/[^/]+/p/.{${PURL_FORM_TOKEN_LENGTH}}$`, 'i');

  function hiCheckboxChecked(el) {
    if (el && (el.state === 'checked' || el.checked === true || el.on === true || el.hasAttribute('checked'))) return true;
    return false;
  }

  function parsedTokenLink(tokenLink) {
    return typeof tokenLink === 'string' ? JSON.parse(tokenLink) : tokenLink;
  }

  function shortenedUrlFor(url, placeholder, tokenLink) {
    if (!tokenLink) return placeholder;

    let uri;
    try {
      uri = new URL(url);
    } catch (e) {
      return url;
    }

    const hostname = uri.hostname.toLowerCase();
    const allowedHost = tokenLink.hosts.some(function(host) { return host.toLowerCase() === hostname; });
    if (!allowedHost) return placeholder;

    if (tokenLink.prefix_pattern) {
      const pathPattern = new RegExp('^(?:' + tokenLink.prefix_pattern + ')$', 'i');
      if (!pathPattern.test(uri.pathname)) return placeholder;
      if (uri.username || uri.password) return url;
      if (tokenLink.reject_fragment && uri.hash) return url;

      return tokenLink.placeholder;
    }

    if (!uri.pathname.startsWith(tokenLink.prefix)) return placeholder;

    const token = uri.pathname.slice(tokenLink.prefix.length);
    const tokenPattern = new RegExp('^(?:' + tokenLink.token_pattern + ')$');
    if (!tokenPattern.test(token) || uri.username || uri.password) return url;
    if (tokenLink.reject_query_and_fragment && (uri.search || uri.hash)) return url;

    return tokenLink.placeholder;
  }

  function estimatedShortenedBody(body, placeholder, tokenLink) {
    if (!placeholder) return body;

    const rule = parsedTokenLink(tokenLink);
    return body.replace(URL_PATTERN, function(match) {
      const url = match.replace(TRAILING_URL_PUNCTUATION, '');
      return shortenedUrlFor(url, placeholder, rule) + match.slice(url.length);
    });
  }

  function linkShorteningOptionsFromDataset(dataset) {
    if (!dataset) return {};

    return {
      shortenedUrlPlaceholder: dataset.shortenedUrlPlaceholder,
      shortenedUrlTokenLink: dataset.shortenedUrlTokenLink,
    };
  }

  function shouldShortenLinks(shortenLinksEnabled, shortenLinksCheckboxId) {
    const checkbox = shortenLinksCheckboxId && document.getElementById(shortenLinksCheckboxId);
    if (checkbox) return hiCheckboxChecked(checkbox);

    return Boolean(shortenLinksEnabled);
  }

  function bodyForSmsLengthValidation(body, {
    shortenLinksEnabled,
    shortenLinksCheckboxId,
    shortenedUrlPlaceholder,
    shortenedUrlTokenLink,
  } = {}) {
    if (!shouldShortenLinks(shortenLinksEnabled, shortenLinksCheckboxId)) return body;

    return estimatedShortenedBody(body, shortenedUrlPlaceholder, shortenedUrlTokenLink);
  }

  function applyMergeReplacements(body, replacements) {
    let result = body;
    for (const { mergeTag, mergeValue } of replacements) {
      if (mergeTag && mergeValue != null) {
        result = result.replaceAll(mergeTag, mergeValue);
      }
    }
    return result;
  }

  function buildSmsPreviewBody(sourceBody, {
    mergeTag,
    mergeValue,
    mergeReplacements,
    micrositeReplacer,
    micrositeFallbackUrl,
    shortenLinksEnabled,
    shortenLinksCheckboxId,
    shortenedUrlPlaceholder,
    shortenedUrlTokenLink,
  } = {}) {
    const replacements = mergeReplacements ?? (
      mergeTag && mergeValue != null ? [{ mergeTag, mergeValue }] : []
    );
    let previewBody = applyMergeReplacements(String(sourceBody ?? ''), replacements);
    if (typeof micrositeReplacer === 'function') {
      previewBody = micrositeReplacer(previewBody, micrositeFallbackUrl);
    }
    return bodyForSmsLengthValidation(previewBody, {
      shortenLinksEnabled,
      shortenLinksCheckboxId,
      shortenedUrlPlaceholder,
      shortenedUrlTokenLink,
    });
  }

  function renderSmsPreview({
    bodyId = 'sms_body',
    body,
    previewId = 'sms_body_preview',
    mergeTag,
    mergeValue,
    mergeReplacements,
    micrositeReplacer,
    micrositeFallbackUrl,
    shortenLinksEnabled,
    shortenedUrlPlaceholder,
    shortenedUrlTokenLink,
    smsMessageLimit,
    errorMessageId,
    ...linkShorteningOptions
  } = {}) {
    const bodyEl = document.getElementById(bodyId);
    const previewEl = document.getElementById(previewId);
    if (!previewEl) return;

    const sourceBody = body != null ? String(body) : String(bodyEl?.value ?? '');
    const previewBody = buildSmsPreviewBody(sourceBody, {
      mergeTag,
      mergeValue,
      mergeReplacements,
      micrositeReplacer,
      micrositeFallbackUrl,
      shortenLinksEnabled,
      shortenedUrlPlaceholder,
      shortenedUrlTokenLink,
      ...linkShorteningOptions,
    });
    previewEl.value = previewBody;
    if (smsMessageLimit != null) {
      updateSmsCharLimitWarning(previewBody, smsMessageLimit, errorMessageId);
    }
  }

  function validatedSmsBodyLength(body, options = {}) {
    return buildSmsPreviewBody(body, options).length;
  }

  function micrositePreviewOptions(fallbackUrl) {
    const dataEl = document.getElementById('textNudgeData');
    const micrositeFallbackUrl = fallbackUrl ?? dataEl?.dataset?.micrositeFallbackUrl;
    const micrositeReplacer = typeof global.smsReplacePersonalizedMicrositeTags === 'function'
      ? global.smsReplacePersonalizedMicrositeTags
      : undefined;

    return { micrositeReplacer, micrositeFallbackUrl };
  }

  function updateSmsCharLimitWarning(previewBody, limit, errorMessageId = 'sms_char_limit_error_message') {
    const errorEl = document.getElementById(errorMessageId);
    if (!errorEl) return;

    if (previewBody.length <= limit) {
      errorEl.classList.add('hide');
    } else {
      errorEl.classList.remove('hide');
    }
  }

  function bindDisableLinkShorteningConfirmation(checkboxId, onToggle) {
    const checkbox = document.getElementById(checkboxId);
    if (!checkbox) return;

    checkbox.addEventListener("toggle-ended", function() {
      if (typeof onToggle === 'function') onToggle();

      if (checkbox.dataset.suppressLinkShorteningToggle === "true") {
        checkbox.dataset.suppressLinkShorteningToggle = "false";
        return;
      }
      if (hiCheckboxChecked(checkbox)) return;

      checkbox.dataset.suppressLinkShorteningToggle = "true";
      checkbox.toggle(true);

      $ip.require("@eab-eip/element/hi-prompt/hi-prompt").then(function() {
        return HIP.fn.prompt({
          title: "Disable Link Shortening?",
          details: "Disabling Link Shortening will also disable tracking URL for SMS. Continue?",
          disallow_close: false,
          controls: [
            {type: "cancel", name: "Cancel"},
            {type: "yes", name: "Continue"}
          ]
        });
      }).then(function() {
        checkbox.dataset.suppressLinkShorteningToggle = "true";
        checkbox.toggle(false);
      }).catch(function() {});
    });
  }

  global.SmsLengthPreview = {
    bindDisableLinkShorteningConfirmation,
    bodyForSmsLengthValidation,
    buildSmsPreviewBody,
    estimatedShortenedBody,
    linkShorteningOptionsFromDataset,
    micrositePreviewOptions,
    renderSmsPreview,
    updateSmsCharLimitWarning,
    validatedSmsBodyLength,
  };
})(window);
